import {
  VaButton,
  VaTextInput,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import React, { useState } from 'react';
import { focusElement } from '@department-of-veterans-affairs/platform-utilities/ui';
import { mockTestingFlagForAPI } from '../../constants';
import { mockInquiryStatusResponse } from '../../utils/mockData';
import { getVAStatusFromCRM } from '../../config/helpers';
import { getInquiryStatus } from '../../utils/api';

/**
 * @param {'success' | 'clock' | 'folder'} name
 * @param {'grey' | 'green'} color
 */
const getIcon = (name, color) => {
  const icon = {
    success: 'check_circle',
    clock: 'schedule',
    folder: 'folder',
  }[name];

  return (
    <span
      className={`vads-u-color--${color} vads-u-margin-left--0p5`}
      aria-hidden="true"
    >
      <va-icon {...{ icon }} size={3} aria-hidden="true" />
    </span>
  );
};

const uiDetailsMap = {
  'In progress': {
    icon: getIcon('clock', 'grey'),
    message: "We're reviewing your question.",
    color: 'grey',
  },
  Replied: {
    icon: getIcon('success', 'green'),
    message:
      "We answered your question. If you did not receive our reply, your question may be a duplicate of another question you've submitted. If you need more help, reply or ask a new question.",
    color: 'green',
  },
  Reopened: {
    icon: getIcon('clock', 'grey'),
    message: "We received your reply. We'll respond soon.",
    color: 'grey',
  },
  Closed: {
    icon: getIcon('folder', 'grey'),
    message:
      'This question was closed because there were no updates for 60 days.',
    color: 'grey',
  },
};

/**
 * @typedef {Object} StatusResponse
 * @property {Object} data
 * @property {string | null} data.id
 * @property {string} data.type
 * @property {Object} data.attributes
 * @property {string} data.attributes.status
 */

export default function StatusChecker() {
  const [statusData, setStatusData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [queryIsValid, setQueryIsValid] = useState(true);
  const [rawInputValue, setRawInputValue] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');

  const focusStatusMessage = () => {
    // Timeout ensures that React has rendered the status message before trying to focus it
    setTimeout(() => focusElement('#status-message'), 0);
  };

  const handleSubmit = () => {
    // Reset before validating new input
    setStatusData({});
    setApiError(false);
    setSubmittedQuery('');

    const trimmedInput = rawInputValue.trim();
    const isValid =
      trimmedInput.length > 0 && /^A-\d{8}-\d{5,10}$/.test(trimmedInput);

    if (!isValid) {
      setQueryIsValid(false);
    } else {
      setQueryIsValid(true);
      setIsLoading(true);
      setSubmittedQuery(trimmedInput);

      // Mocking the API response for testing when searching for reference number
      // A-20250106-308944
      if (mockTestingFlagForAPI && trimmedInput === 'A-20250106-308944') {
        setStatusData(mockInquiryStatusResponse.data);
        setIsLoading(false);
        focusStatusMessage();
      } else {
        getInquiryStatus(trimmedInput)
          .then(res => {
            setStatusData(res.data);
            setIsLoading(false);
            focusStatusMessage();
          })
          .catch(() => {
            setIsLoading(false);
            setApiError(true);
            focusStatusMessage();
          });
      }
    }
  };

  const questionStatus = () => {
    if (isLoading) {
      return (
        <va-loading-indicator
          data-testid="loading-indicator"
          message="Loading question status..."
        />
      );
    }

    if (apiError) {
      return (
        <div id="status-message">
          <p>
            We couldn’t find a question with the reference number "
            {submittedQuery}
            ."
          </p>
          <p>
            Check the confirmation email we sent you to make sure you entered it
            correctly. If it still doesn’t work, you can ask the same question
            again and include your original reference number.
          </p>
        </div>
      );
    }

    if (statusData?.attributes?.status) {
      const rawStatus = statusData.attributes.status;
      const uiStatus = getVAStatusFromCRM(rawStatus);
      const uiDetails = uiDetailsMap[uiStatus];

      return (
        <div>
          <p id="status-message">
            Showing the status for reference number "{submittedQuery}."
          </p>
          <div className="vads-u-border-bottom--1px vads-u-border-color--gray-light" />
          {uiDetails?.message ? (
            <dl>
              <dt className="vads-u-margin-bottom--1">
                <span className="vads-u-font-weight--bold">Status: </span>
                {uiStatus} {uiDetails?.icon}
              </dt>

              <dd
                className={`vads-u-border-left--5px vads-u-padding--0p5 vads-u-border-color--${uiDetails?.color}`}
              >
                <div className="vads-u-margin--2 vads-u-margin-right--0">
                  {uiDetails.message}
                </div>
              </dd>
            </dl>
          ) : (
            <p>
              <span className="vads-u-font-weight--bold">Status: </span>
              {uiStatus}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div data-dd-privacy="hidden">
      <h2 className="vads-u-margin-top--4">
        Check the status of your question
      </h2>
      <VaTextInput
        label="Reference number"
        width="lg"
        name="reference-number"
        value={rawInputValue}
        hint="Enter the reference number from the confirmation email we sent you. Example: A-20260701-3939501"
        onInput={e => setRawInputValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleSubmit();
          }
        }}
        error={
          queryIsValid
            ? undefined
            : 'Enter a valid reference number, including dashes'
        }
      />
      <div className="vads-u-margin-top--2 vads-u-margin-bottom--3">
        <VaButton text="Check status" onClick={handleSubmit} />
      </div>
      <div className="vads-u-margin-bottom--7">{questionStatus()}</div>
    </div>
  );
}
